'use client';

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { FeedAlertTrigger } from '@/new_components/alerts/FeedAlertTrigger';
import { legalFormLabel } from '@/new_components/alerts/alert-format';

/**
 * Legal forms offered for the new-companies alert. Values are the register's
 * rechtsform codes (see `RECHTSFORM_OPTIONS`), plus the backend's pseudo
 * value `UG` — UGs carry the GmbH code, so the feed separates them by name.
 */
const LEGAL_FORM_CHOICES = ['221110', 'UG', '112211', '222110', '411000', '112100'];
const ALL = 'all';

/**
 * The client island of the SSR Neugründungen hub: "Alert für neue
 * Unternehmen", optionally narrowed to one legal form.
 */
export function NewCompaniesAlertButton() {
  const { t } = useTranslation();
  const [legalForm, setLegalForm] = useState<string>(ALL);
  const filters: Record<string, unknown> = { country: 'DE' };
  if (legalForm !== ALL) filters.legal_forms = [legalForm];

  return (
    <div className="flex flex-col gap-2 lg:flex-row lg:items-center">
      <Select value={legalForm} onValueChange={setLegalForm}>
        <SelectTrigger
          className="h-11 w-full rounded-lg text-sm lg:h-9 lg:w-56"
          aria-label={t('alerts.newCompanies.legalForm')}
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL} className="min-h-11 lg:min-h-9">
            {t('alerts.newCompanies.allLegalForms')}
          </SelectItem>
          {LEGAL_FORM_CHOICES.map((code) => (
            <SelectItem key={code} value={code} className="min-h-11 lg:min-h-9">
              {legalFormLabel(code)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <FeedAlertTrigger
        feedType="new_companies"
        filters={filters}
        isUnfiltered={legalForm === ALL}
        unfilteredDescription={t('alerts.trigger.unfilteredNewCompanies')}
        label={t('alerts.newCompanies.button')}
        signInLabel={t('alerts.newCompanies.signIn')}
      />
    </div>
  );
}
